import React, {useState, useEffect, useRef, useCallback, useMemo} from "react";
import styled from "styled-components";
import {FieldDataType, XyiconFeature, Permission} from "../../../generated/api/base";
import {NavigationEnum, SettingsModuleNav, SettingsSubNav} from "../../../Enums";
import type {IModel} from "../../../data/models/Model";
import type {ISpotlightRule} from "../../../data/models/View";
import type {IFilterRowFilter} from "../../../data/models/filter/Filter";
import type {IFieldPointer} from "../../../data/models/field/Field";
import type {FilterOperator} from "../../../data/models/filter/operator/FilterOperator";
import {HorizontalAlignment} from "../../../utils/dom/DomUtils";
import {useAppStore} from "../../../StateManager";
import {SelectInputV5} from "../input/select/SelectInputV5";
import FieldCondtionIcon from "../icons/spotlight-field-condition-icon.svg?react";
import type {ListObject} from "../../../data/models/field/datatypes/SingleSelect";
import {baseDistance, fontSize, radius, fontWeight, ELLIPSIS} from "../styles/styles";
import DragIcon from "../icons/drag.svg?react";
import {colorPalette} from "../styles/colorPalette";
import {ToggleOptionV5} from "../details/ToggleOptionV5";
import {IconButtonV5} from "../interaction/IconButtonV5";
import TrashIcon from "../icons/trash.svg?react";
import GearIcon from "../icons/gear.svg?react";
import CirclePlusIcon from "../icons/circle-plus.svg?react";
import {ButtonV5} from "../button/ButtonV5";
import {AdvancedFilterFieldV5, CriteriaInputsStyled} from "../filter/advance/AdvancedFilterFieldV5";
import {LogicalSeparators} from "../../../data/models/filter/LogicalSeparators";
import {LogicalSeparator} from "../../../data/models/filter/LogicalSeparator";
import type {ColorRuleCategory} from "../../modules/space/spaceeditor/ui/viewbar/ColorRules";
import {AdvancedFilterFieldStyled} from "../filter/advance/AdvancedFilterField.styled";
import {InfoBubbleV5} from "../button/InfoBubbleV5";
import {ReactUtils} from "../../utils/ReactUtils";
import {InfoButtonV5} from "../button/InfoButtonV5";
import {FilterOperatorsV5} from "../filter/operator/FilterOperatorsV5";
import {StringParamV5} from "../filter/advance/controls/StringParamV5";
import {FieldDataTypes} from "../../../data/models/field/FieldDataTypes";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {constructFilterTitle, getAllFieldsForSpotlight, isFilterComplete} from "./spotlightUtils";

interface ISpotlightFieldBasedRulesV5Props {
	readonly rule: ISpotlightRule;
	readonly feature: XyiconFeature;
	readonly items: IModel[];
	readonly onRuleUpdate?: (ruleId: string, updates: Partial<ISpotlightRule>) => void;
	readonly onRuleDelete?: (ruleId: string) => void;
	readonly openInSpaceEditor?: boolean;
	readonly onExpandChange?: (ruleId: string, isExpanded: boolean) => void;
	readonly isExpanded?: boolean; // Passed from parent state
}

export const SpotlightFieldBasedRulesV5 = (props: ISpotlightFieldBasedRulesV5Props) => {
	const {rule, feature, items, onRuleUpdate, onRuleDelete, openInSpaceEditor, onExpandChange, isExpanded = true} = props;
	const appState = useAppStore((state) => state.appState);

	const [selectedFieldRefId, setSelectedFieldRefId] = useState<string>("");
	const [showAllOptions, setShowAllOptions] = useState<boolean>(false);

	const handleToggleExpanded = useCallback(() => {
		const newValue = !isExpanded;
		// Notify parent to update state (parent will handle localStorage)
		onExpandChange?.(rule.id, newValue);
	}, [isExpanded, rule.id, onExpandChange]);

	const [showTitleTooltip, setShowTitleTooltip] = useState<boolean>(false);

	const fieldOptionsExpandedListRef = useRef<HTMLDivElement>(null);
	const ruleTitleRef = useRef<HTMLDivElement>(null);
	const ruleTitleTextRef = useRef<HTMLSpanElement>(null);

	const handleDeleteRule = () => {
		if (onRuleDelete) {
			onRuleDelete(rule.id);
		}
	};

	const handleSettingsClick = () => {
		if (!rule.fieldRefID) {
			return;
		}

		const field = appState.actions.getFieldByRefId(rule.fieldRefID);
		if (!field) {
			return;
		}

		const getFeatureString = (feature: XyiconFeature): SettingsModuleNav => {
			switch (feature) {
				case XyiconFeature.Xyicon:
					return SettingsModuleNav.XYICON;
				case XyiconFeature.Boundary:
					return SettingsModuleNav.BOUNDARY;
				case XyiconFeature.XyiconCatalog:
					return SettingsModuleNav.CATALOG;
				case XyiconFeature.Space:
					return SettingsModuleNav.SPACE;
				case XyiconFeature.Portfolio:
					return SettingsModuleNav.PORTFOLIO;
				default:
					return SettingsModuleNav.XYICON;
			}
		};

		const featureString = getFeatureString(field.feature);
		// Store the field ID in localStorage so the settings page can select it after navigation
		const fieldSelectionKey = `spotlight-field-selection-${featureString}`;
		localStorage.setItem(fieldSelectionKey, rule.fieldRefID);
		// URL structure: /#app/settings/{feature}/fields
		appState.app.navigation.goApp({
			mainNav: NavigationEnum.SETTINGS,
			subNavs: [SettingsSubNav.MODULES, featureString, "fields"],
		});
	};

	const onIndicatorTypeChange = (indicatorType: ColorRuleCategory) => {
		if (onRuleUpdate) {
			onRuleUpdate(rule.id, {indicatorType});
		}
	};

	useEffect(() => {
		if (!showAllOptions) {
			return;
		}

		const handleClickOutside = (event: MouseEvent) => {
			if (fieldOptionsExpandedListRef.current && !fieldOptionsExpandedListRef.current.contains(event.target as Node)) {
				setShowAllOptions(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [showAllOptions]);

	const allFields = getAllFieldsForSpotlight(appState, feature);

	const getFieldRefIds = (): IFieldPointer[] => allFields.map((f) => f.refId);

	const onChangeFieldInFilter = (row: IFilterRowFilter, fieldPointer: IFieldPointer) => {
		const field = appState.actions.getFieldByRefId(fieldPointer);

		if (field) {
			const operators = FieldDataTypes.map[field.dataType].operators(field.dataTypeSettings);
			// Sort operators alphabetically by label, then select the first one
			const sortedOperators = [...operators].sort((a, b) => {
				const labelA = FilterOperatorsV5.map[a]?.label || "";
				const labelB = FilterOperatorsV5.map[b]?.label || "";
				return StringUtils.sortIgnoreCase(labelA, labelB);
			});
			row.value.field = fieldPointer;
			row.value.operator = sortedOperators[0];
			row.value.param = "";
			updateRuleAndRefresh();
		}
	};

	const onChangeOperatorInFilter = (row: IFilterRowFilter, operator: FilterOperator) => {
		row.value.operator = operator;
		row.value.param = "";
		updateRuleAndRefresh();
	};

	const shouldDeferParamUpdate = (row: IFilterRowFilter) => {
		if (!row?.value) {
			return false;
		}

		if (!row.value.operator) {
			return true;
		}

		const operatorConfig = FilterOperatorsV5.map[row.value.operator];
		if (!operatorConfig?.control) {
			return false;
		}

		return operatorConfig.control === StringParamV5;
	};

	const onChangeParamsInFilter = (row: IFilterRowFilter, param: any) => {
		row.value.param = param ?? "";

		if (!shouldDeferParamUpdate(row)) {
			updateRuleAndRefresh();
		}
	};

	const onBlurParamsInFilter = () => {
		updateRuleAndRefresh();
	};

	const onRemoveFieldFromFilter = (index: number) => {
		if (index >= 0) {
			rule.filters.splice(index, 1);
		}

		const nextSeparator = rule.filters[index];
		if (nextSeparator?.type === "separator") {
			rule.filters.splice(index, 1);
		}

		while (rule.filters[rule.filters.length - 1]?.type === "separator") {
			rule.filters.pop();
		}

		updateRuleAndRefresh();
	};

	const onAddConditionToRule = () => {
		if (rule.filters.length > 0) {
			rule.filters.push({
				type: "separator",
				value: LogicalSeparator.AND,
			});
		}

		rule.filters.push({
			type: "filter",
			value: {
				field: "",
				operator: "" as any,
				param: "",
			},
		});

		updateRuleAndRefresh();
	};

	const constructRuleTitle = (): string => {
		const fieldName = selectedFieldObject?.name || "";
		const baseTitle = fieldName ? `Color by ${fieldName}` : "Define Spotlight";

		const completeFilters = rule.filters.filter((filter) => {
			if (filter.type === "separator") {
				return true;
			}
			return isFilterComplete(filter);
		});

		const filterString = constructFilterTitle(completeFilters, appState);

		if (filterString) {
			return `${baseTitle} where ${filterString}`;
		}

		return baseTitle;
	};

	const updateRuleAndRefresh = () => {
		if (onRuleUpdate) {
			onRuleUpdate(rule.id, {
				filters: rule.filters,
			});
		}
	};

	const selectedFieldObj =
		rule.fieldRefID && !allFields.some((f) => f.refId === rule.fieldRefID) ? appState.actions.getFieldByRefId(rule.fieldRefID) : null;

	const singleSelectFields = [...allFields, ...(selectedFieldObj ? [selectedFieldObj] : [])].filter(
		(field, index, arr) => field?.dataType === FieldDataType.SingleChoiceList && arr.findIndex((f) => f.refId === field.refId) === index,
	);
	const selectedFieldObject = singleSelectFields.find((field) => field.refId === selectedFieldRefId);
	const selectedChoiceList: ListObject[] = selectedFieldObject?.dataTypeSettings?.choiceList || [];

	const stringifyField = (refId: string) => {
		const field = singleSelectFields.find((f) => f.refId === refId);
		return field?.name || refId;
	};

	// Calculate field permission using useMemo for stable values
	const fieldPermissionInfo = useMemo(() => {
		const {user} = appState;
		if (!rule.fieldRefID) {
			return {
				permission: Permission.View,
				hasAccess: true,
				canView: true,
				canEdit: false,
			};
		}

		const fieldForPermission = appState.actions.getFieldByRefId(rule.fieldRefID);
		if (!fieldForPermission) {
			return {
				permission: Permission.None,
				hasAccess: false,
				canView: false,
				canEdit: false,
			};
		}

		// Only pass items if they match the field's feature
		// This ensures permission checks use the correct feature context
		// If items are from a different feature (e.g., Xyicon items when checking Boundary field),
		// pass undefined to fall back to field-level permissions only
		const itemsForPermission = items.length > 0 && items.every((item) => item.ownFeature === fieldForPermission.feature) ? items : undefined;

		// Check permission with items array (only if items match field's feature, otherwise undefined)
		const fieldPermission = appState.actions.getFieldPermission(fieldForPermission, itemsForPermission);

		return {
			permission: fieldPermission,
			// hasAccess: user can view the field (View permission or higher)
			hasAccess: fieldPermission >= Permission.View,
			// canView: user can see field options (View permission or higher)
			canView: fieldPermission >= Permission.View,
			// canEdit: user can edit field settings (only admin with Update permission)
			canEdit: fieldPermission >= Permission.Update && user?.isAdmin,
		};
	}, [rule.fieldRefID, items, appState]);

	const renderSelectedField = (fieldRefId: string) => {
		if (!fieldRefId) {
			return "Field";
		}

		const field = appState.actions.getFieldByRefId(fieldRefId) || singleSelectFields.find((f) => f.refId === fieldRefId);
		const fieldName = field?.name || fieldRefId || "Field";

		return (
			<div className="selected-field-container">
				<span>{fieldName}</span>
				{!fieldPermissionInfo.hasAccess && (
					<InfoButtonV5
						iconStyle={{height: "16px", width: "16px", color: colorPalette.negative.c500Primary}}
						bubbleText="You do not have permission to use this field. Please contact an admin."
					/>
				)}
			</div>
		);
	};

	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect -- selected field state mirrors the externally managed rule.
		setSelectedFieldRefId(rule.fieldRefID || "");
	}, [rule.fieldRefID]);

	const onChangeField = (fieldRefId: string) => {
		setShowAllOptions(false);
		const selectedFieldObj = singleSelectFields.find((field) => field.refId === fieldRefId);

		if (selectedFieldObj && onRuleUpdate) {
			const defaultIndicatorType: ColorRuleCategory = rule.indicatorType === "indicator" ? "indicator" : "highlight";

			onRuleUpdate(rule.id, {
				fieldRefID: selectedFieldObj.refId,
				label: `Color by ${selectedFieldObj.name}`,
				indicatorType: defaultIndicatorType,
			});
		}
	};

	const maxVisibleItems = 10;
	const displayedChoices = showAllOptions ? selectedChoiceList : selectedChoiceList.slice(0, maxVisibleItems);
	const hasMoreItems = selectedChoiceList.length > maxVisibleItems;
	const ruleTitle = constructRuleTitle();

	const checkIfTitleTruncated = useCallback(() => {
		if (ruleTitleTextRef.current && ruleTitleRef.current) {
			const scrollWidth = ruleTitleTextRef.current.scrollWidth;
			const clientWidth = ruleTitleTextRef.current.clientWidth;
			const isOverflowing = scrollWidth > clientWidth;
			setShowTitleTooltip(isOverflowing);
		}
	}, []);

	return (
		<FieldBasedRuleContainer data-rule-id={rule.id}>
			<div className="field-rule-header">
				<div className="color-title-drag-container">
					<div className="drag-handle">
						<DragIcon className="drag-icon" />
					</div>
					<div
						ref={ruleTitleRef}
						className="field-rule-title"
						onClick={handleToggleExpanded}
						onMouseEnter={checkIfTitleTruncated}
						onMouseLeave={() => setShowTitleTooltip(false)}
						style={{cursor: "pointer", position: "relative"}}
					>
						<div style={{width: "24px", height: "24px"}}>
							<FieldCondtionIcon />
						</div>
						<span
							ref={ruleTitleTextRef}
							className="field-rule-title-text"
						>
							{ruleTitle}
						</span>
						{showTitleTooltip && (
							<InfoBubbleV5
								content={ruleTitle}
								style={{maxWidth: "528px"}}
								anchorRef={ruleTitleRef}
							/>
						)}
					</div>
				</div>
				<div className="toggle-container">
					{isExpanded && openInSpaceEditor && (
						<>
							<ToggleOptionV5
								key={`indicator-${rule.id}`}
								options={["Highlight", "Indicator"]}
								render={(option) => option}
								onChange={(value) => {
									const indicatorType = value.toLowerCase() as "highlight" | "indicator";
									onIndicatorTypeChange(indicatorType);
								}}
								selectedOption={rule.indicatorType === "indicator" ? "Indicator" : "Highlight"}
								className="spotlight-toggle-option"
							/>
						</>
					)}
					{!isExpanded && onRuleDelete && (
						<div style={{display: "flex", alignItems: "center", gap: baseDistance.sm}}>
							<IconButtonV5
								IconComponent={TrashIcon}
								onClick={handleDeleteRule}
								title="Delete"
							/>
						</div>
					)}
				</div>
			</div>
			{isExpanded && (
				<div className="field-rule-content">
					<div className="field-rule-settings">
						<SelectInputV5
							selected={selectedFieldRefId}
							onChange={onChangeField}
							options={singleSelectFields.map((field) => field.refId)}
							renderOptionWhenClosed={renderSelectedField}
							renderOptionWhenOpen={stringifyField}
							stringifyOption={stringifyField}
							placeholder="Field"
							sort={true}
							searchBarMode="when many items"
							isSameWidth={true}
							className="field-rule-select"
						/>
						<div className="field-rule-settings-buttons">
							{selectedFieldRefId && fieldPermissionInfo.canEdit ? (
								<IconButtonV5
									IconComponent={GearIcon}
									onClick={handleSettingsClick}
									title="Settings"
								/>
							) : selectedFieldRefId && !fieldPermissionInfo.canEdit ? (
								<InfoButtonV5
									icon="gear"
									iconStyle={{height: "16px", width: "16px", color: colorPalette.gray.c300}}
									bubbleText="You don’t have permission to edit this field. Ask an admin for changes."
									horizontalOpen={HorizontalAlignment.right}
									infoBubbleStyle={{maxWidth: "528px"}}
								/>
							) : null}
							<IconButtonV5
								IconComponent={TrashIcon}
								onClick={handleDeleteRule}
								title="Delete"
							/>
						</div>
					</div>
					{selectedFieldRefId && (
						<div className="field-options-container">
							{fieldPermissionInfo.canView && (
								<>
									<div className={ReactUtils.cls("field-options-header", {"list-expanded": showAllOptions})}>
										<span className="field-options-label">Field Options</span>
									</div>
									<div
										ref={showAllOptions ? fieldOptionsExpandedListRef : null}
										className={ReactUtils.cls("field-options-list", {expanded: showAllOptions})}
									>
										{(showAllOptions ? selectedChoiceList : displayedChoices).map((choice, index) => {
											const {color, value} = choice;
											return (
												<FieldColorStyled
													key={index}
													$color={color}
												>
													<span className="field-color-value">{value}</span>
												</FieldColorStyled>
											);
										})}
									</div>
								</>
							)}

							{hasMoreItems && !showAllOptions && fieldPermissionInfo.canView && (
								<button
									className="show-more-button"
									onClick={() => setShowAllOptions(true)}
								>
									Show More
								</button>
							)}
							{fieldPermissionInfo.canView && (
								<div className="conditional-filters-section">
									{rule.filters.map((row, index) => {
										if (row.type === "filter") {
											const isFieldEmpty = !row.value.field;
											const isOperatorEmpty = !row.value.operator;
											const operatorTooltipMessage = isFieldEmpty ? "Select a field first" : isOperatorEmpty ? "Select a condition first" : "";
											return (
												<AdvancedFilterFieldV5
													key={`${index}-${row.value.field}`}
													fieldRefIds={getFieldRefIds()}
													filter={row.value}
													onChangeField={(field) => onChangeFieldInFilter(row, field)}
													onChangeOperator={(operator) => onChangeOperatorInFilter(row, operator)}
													onChangeParams={(params) => onChangeParamsInFilter(row, params)}
													onBlurParams={onBlurParamsInFilter}
													onRemove={() => onRemoveFieldFromFilter(index)}
													linkedFields={false}
													disableOperator={isFieldEmpty}
													disableParam={isFieldEmpty || isOperatorEmpty}
													operatorTooltip={operatorTooltipMessage}
												>
													<div>
														{rule.filters[index - 1]?.type === "separator" ? (
															<ToggleOptionV5
																key={`${index - 1}-${rule.filters[index - 1].value}`}
																options={LogicalSeparators.array}
																render={(separator) => separator.label}
																onChange={(value) => {
																	rule.filters[index - 1].value = value.id;
																	updateRuleAndRefresh();
																}}
																selectedOption={LogicalSeparators.array.find((separator) => separator.id === rule.filters[index - 1].value)?.label}
																className="spotlight-toggle-option"
															/>
														) : (
															<span>Where</span>
														)}
													</div>
												</AdvancedFilterFieldV5>
											);
										}
										return null;
									})}
									<div className="add-condition-btn-container">
										<ButtonV5
											label="Add Condition"
											type="tertiary"
											onClick={onAddConditionToRule}
											className="add-condition-btn"
											disabled={!rule.filters.every((f) => f.type === "separator" || isFilterComplete(f))}
										>
											<CirclePlusIcon />
										</ButtonV5>
									</div>
								</div>
							)}
							{!fieldPermissionInfo.hasAccess && (
								<PermissionMessageStyled>You do not have permission to use this field. Please contact an admin.</PermissionMessageStyled>
							)}
						</div>
					)}
				</div>
			)}
		</FieldBasedRuleContainer>
	);
};

const FieldBasedRuleContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.sm};
	background-color: white;
	width: 100%;
	position: relative;
	box-sizing: border-box;
	overflow: visible;
	padding-bottom: ${baseDistance.sm};

	&::after {
		content: "";
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		height: 1px;
		background-color: ${colorPalette.gray.c100};
	}

	&:last-child::after {
		display: none;
	}

	.field-rule-header {
		margin-left: ${baseDistance.md};
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-right: ${baseDistance.md};
		overflow: visible;

		.color-title-drag-container {
			width: 100%;
			display: flex;
			align-items: center;
			gap: ${baseDistance.sm};
			overflow: visible;
		}

		.toggle-container {
			height: 24px;
			display: flex;
			align-items: center;
			justify-content: flex-end;
			width: 100%;
			gap: ${baseDistance.sm};

			.spotlight-toggle-option {
				background-color: ${colorPalette.gray.c100};
				padding: ${baseDistance.xxs};

				.toggle-button {
					background-color: ${colorPalette.gray.c100};

					&.active {
						background-color: ${colorPalette.primary.c200Light};
					}
				}
			}

			.IconButton {
				width: 24px;
				height: 24px;

				svg {
					width: 16px;
					height: 16px;
				}
			}
		}

		.toggle-button {
			width: auto;
		}
	}

	.field-rule-title {
		display: flex;
		align-items: center;
		gap: ${baseDistance.xs};
		max-width: 530px;
		overflow: visible;

		.svg {
			width: 16px;
			height: 16px;
		}

		.field-rule-title-text {
			${ELLIPSIS}
			font-size: ${fontSize.md};
			font-weight: ${fontWeight.bold};
			color: ${colorPalette.gray.c950};
			line-height: ${baseDistance.md};
			padding-right: ${baseDistance.sm};
		}
	}

	.field-rule-content {
		display: flex;
		flex-direction: column;
		gap: ${baseDistance.sm};
		padding: ${baseDistance.sm} 0 ${baseDistance.sm} ${baseDistance.sm};
		border-radius: ${radius.md};
		margin-left: 48px;
		max-height: 276px;
		max-width: 612px;
		background-color: ${colorPalette.gray.c50};

		.field-rule-settings {
			display: flex;
			align-items: center;
			gap: ${baseDistance.sm};
			justify-content: space-between;

			.selected-field-container {
				display: flex;
				align-items: center;
				justify-content: space-between;
				width: 100%;
				height: 100%;
			}
		}

		.field-rule-settings-buttons {
			display: flex;
			align-items: center;
			gap: ${baseDistance.sm};
			margin-right: ${baseDistance.sm};

			.IconButton {
				min-width: 24px;
				width: 24px;
				height: 24px;

				svg {
					width: 16px;
					height: 16px;
				}
			}
		}

		.field-options-container {
			display: flex;
			flex-direction: column;
			gap: ${baseDistance.sm};
			align-items: flex-start;
			overflow-y: auto;
			max-height: 276px;
			overflow-x: hidden;
		}
	}

	.field-options-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-top: ${baseDistance.xs};

		&.list-expanded {
			display: none;
		}
	}

	.field-options-label {
		font-size: ${fontSize.lg};
		font-weight: ${fontWeight.bold};
		color: ${colorPalette.gray.c700Dark};
	}

	.show-more-button {
		font-size: ${fontSize.md};
		font-weight: ${fontWeight.semiBold};
		color: ${colorPalette.gray.c700Dark};
		background: none;
		border: none;
		cursor: pointer;
		padding: 0;
		text-decoration: underline;
	}

	.field-options-list {
		display: grid;
		grid-template-columns: repeat(2, 278px);
		grid-auto-flow: column;
		grid-template-rows: repeat(5, auto);
		gap: ${baseDistance.xs};
		overflow-y: visible;

		&.expanded {
			width: 100%;
			min-height: 188px;
			position: relative;
			grid-auto-flow: row;
			grid-template-rows: auto;
			overflow-y: auto;
			overflow-x: hidden;
			background-color: ${colorPalette.white};
			border-radius: ${radius.md};
			box-shadow: 0px 4px 20px 0px #00000033;
			padding: ${baseDistance.md};
		}
	}

	.field-rule-select {
		width: 240px;
		background-color: ${colorPalette.white};
	}

	.conditional-filters-section {
		display: flex;
		flex-direction: column;
		gap: ${baseDistance.sm};

		${AdvancedFilterFieldStyled} {
			padding: 0;
			background-color: transparent;

			${CriteriaInputsStyled} {
				grid-template-columns: 54px 156px 128px 200px 20px;

				input {
					background-color: ${colorPalette.white};
					border: none;

					&::placeholder {
						color: ${colorPalette.gray.c300};
					}
				}

				.header {
					width: 54px;
					background-color: transparent;
				}

				.toggle-button {
					width: 24px;
				}

				.toggle-button.active {
					width: 30px;
				}

				.IconButton {
					width: 24px;
					height: 24px;

					svg {
						width: 16px;
						height: 16px;
					}
				}

				.SelectInput {
					background-color: ${colorPalette.white};
					border: none;

					&.disabled {
						color: ${colorPalette.gray.c300};
					}
				}

				.spotlight-toggle-option {
					background-color: ${colorPalette.gray.c100};
					padding: ${baseDistance.xxs};

					.toggle-button {
						background-color: ${colorPalette.gray.c100};
					}

					.toggle-button.active {
						background-color: ${colorPalette.primary.c200Light};
					}
				}
			}
		}
	}

	.conditional-filters-header {
		span {
			font-size: ${fontSize.sm};
			font-weight: ${fontWeight.semiBold};
			color: ${colorPalette.gray.c600};
		}
	}

	.add-condition-btn-container {
		display: flex;
		justify-content: flex-start;
		padding: ${baseDistance.xs} 0;

		.add-condition-btn {
			font-size: ${fontSize.md};
			font-weight: ${fontWeight.normal};
			cursor: pointer;
			justify-content: left;
			border: none;

			&:hover {
				color: none;
				background-color: none;
			}
		}
	}
`;

const FieldColorStyled = styled.div<{$color?: string}>`
	display: flex;
	align-items: center;
	gap: ${baseDistance.xs};
	font-size: ${fontSize.md};
	height: 24px;

	.field-color-value {
		${ELLIPSIS}
		max-width: 278px;
		width: 100%;
		line-height: 16px;
	}

	&::before {
		content: "";
		display: inline-block;
		width: 16px;
		height: 16px;
		border-radius: 50%;
		margin-right: ${baseDistance.xs};
		background: ${(props) =>
			props.$color
				? `#${props.$color}`
				: `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAAAXNSR0IArs4c6QAAAONJREFUeF7t20EOhEAIRFG4/6F7DvEnYeFzryQIv6pBd2behOu9dPvsbog+k+NLgArQAqmJcw9iAAhSgZKB3IJkkAySQTJ4CiE+gA8oBeg0mH3Ai084P89HhqwEqIA209ICsQdjAeaZIgaAYKxBDMCAYy8fXwAIgiAIcoJpJEYGI4VjB3YrbC9gL2AvkCB43cM5PgZgAAZgQFnNZAhdGykQBEEQBEEQDBmgAm2glM/z+QUYisYUGoldO7kY32IEAzCg6RgIRgjFAsw+AgRBMNYgBmCAT2TCYfoPPz/HCqQCX1eBHzHnv7C7WhBSAAAAAElFTkSuQmCC")center/cover no-repeat`};
	}
`;

const PermissionMessageStyled = styled.div`
	display: flex;
	width: 100%;
	align-items: center;
	font-size: ${fontSize.md};
	font-weight: ${fontWeight.bold};
	color: ${colorPalette.gray.c700Dark};
	min-height: 24px;
`;
